import React, { useCallback, useContext, useEffect, useState } from 'react';
import { GoClock, GoCalendar, GoGlobe, GoLock } from "react-icons/go";
import Cropper from "../../../../src/cropper/components/CroppersWizard.tsx";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MobileTimePicker, MobileDatePicker } from "@mui/x-date-pickers";
import { TextField, InputAdornment } from '@mui/material';
import { RiImageAddLine, RiSparkling2Fill, RiLiveLine } from 'react-icons/ri';
import { IoCloseSharp } from 'react-icons/io5';
import { Language, PostDetailContext } from '../../../App';
import { _imagesURL, adminBaseURL } from '../../../constants/API';
import { authorize } from '../../../shared/authorize';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useCustomToast } from '../../common/CustomToast';
import moment from 'moment';
import { debounce } from 'lodash';
import { getToken } from '../../../shared/token.js';
import ImageShowModal from '../../common/ImageShowModal.jsx';
import { useCheckSubscriptionPermission } from '../../../hooks/useCheckSubscriptionPermission';
import './formTheme.css';

const WebinarTab = ({ onHide, setProgress, handleApiCall, editWebinar, selectedPost, refetch, setEditWebinar, handlePostSelect }) => {
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();
  const { updatePostDetail } = useContext(PostDetailContext);
  const checkPermission = useCheckSubscriptionPermission('upload_webinar');

  const SplashData = JSON.parse(localStorage.getItem("SplashData") || "{}");
  const subcategories = SplashData?.home?.subcategories || [];

  // States for Image Preview in Modal
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const toggleImagePreview = () => setIsImagePreviewOpen(!isImagePreviewOpen);
  const handleImageClick = (img) => {
    setPreviewImage(img);
    toggleImagePreview();
  };

  // Form field state values
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [scheduleTime, setScheduleTime] = useState("");
  const [timeLimit, setTimeLimit] = useState('');
  const [visibility, setVisibility] = useState('Public');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inviteList, setInviteList] = useState([]);
  const [invitePage, setInvitePage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [inviteSpin, setInviteSpin] = useState(false);
  const [webinarId, setWebinarId] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [keyword, setKeyword] = useState("");
  const [inviteData, setInviteData] = useState({});
  const [searchData, setSearchData] = useState({});
  const [searchList, setSearchList] = useState([]);
  // Temporary states to hold values while picker is open
  const [tempDate, setTempDate] = useState(null);
  const [tempTime, setTempTime] = useState(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [images, setImages] = useState([]);
  const [coverImg, setCoverImg] = useState([]);
  const [cropperOpen, setCropperOpen] = useState(false);
  const maxNumber = 2;
  const [ar, setAR] = useState(1);
  const [maxARatio, setMaxAR] = React.useState(undefined);
  const [minARatio, setMinAR] = React.useState(undefined);
  const [resizable, setResizable] = useState(true);
  const [hasSettings, setHasSettings] = useState(true);
  const [updateCoordinatesCheck, setUpdateCoordinatesCheck] = useState(false);
  const [fileUploadRefs, setFileUploadRefs] = React.useState({});
  const [numberOfImages, setNumberOfImages] = React.useState(
    (Array.isArray(coverImg) ? coverImg.length : Object.keys(coverImg || {}).length) < maxNumber
      ? (Array.isArray(coverImg) ? coverImg.length : Object.keys(coverImg || {}).length)
      : maxNumber
  );

  const [height, setHeight] = React.useState(undefined);
  const [width, setWidth] = React.useState(undefined);
  const [top, setTop] = React.useState(undefined);
  const [left, setLeft] = React.useState(undefined);

  useEffect(() => {
    if (editWebinar && selectedPost) {
      setTitle(selectedPost.title || '');
      setCategory(selectedPost.subcategory_id?.toString() || '');
      setDescription(selectedPost.caption || '');
      setTimeLimit(selectedPost.duration || '');
      setVisibility(selectedPost.visibility || 'Public');

      if (selectedPost.start_timestamp) {
        const date = new Date(selectedPost.start_timestamp * 1000);
        setStartDate(moment(date));
        setScheduleDate(moment(date).format("YYYY-MM-DD"));
        const momentDate = moment(date);
        setStartTime(momentDate.clone().set({ hour: date.getHours(), minute: date.getMinutes(), second: 0 }));
        setScheduleTime(selectedPost.schedule_time);
      }
      if (selectedPost.media && selectedPost.media.length > 0) {
        setCoverImg(selectedPost.media);
      }
    }
  }, [editWebinar, selectedPost]);

  const handleClose = () => {
    setCropperOpen(false);
  };

  function isValidFutureDate(inputDate) {
    const userDate = new Date(inputDate);
    if (isNaN(userDate.getTime())) {
      showToast(lang?.error_note_number || "Invalid date", "error");
      return false;
    }
    const today = new Date();
    userDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (userDate < today) {
      showToast(lang?.date_should_greater_todays_date || "Date should be today or a future date", "error");
      return false;
    }
    return true;
  }

  const handleDateChange = (newDate) => {
    setTempDate(newDate);
    setStartDate(newDate);
    setScheduleDate(moment(newDate).format("YYYY-MM-DD"));
  };

  const handleTimePickerOpen = () => {
    if (editWebinar) return;
    setTempTime(moment().add(5, 'minutes'));
    setStartTime(moment().add(5, 'minutes'));
    setScheduleTime(moment().add(5, 'minutes').format("hh:mm A"));
  };

  const handleTimeChange = (newTime) => {
    setTempTime(newTime);
    setStartTime(newTime);
    setScheduleTime(moment(newTime).format("hh:mm A"));
  };

  const handleTimeOpen = (type) => {
    if (type === "start_time") {
      showToast(lang?.select_start_date_first || "Select start date first!", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const combinedDateTimeString = `${scheduleDate} ${scheduleTime}`;
    const combinedDateTime = moment(combinedDateTimeString, "YYYY-MM-DD hh:mm A");
    const start_timestamp = combinedDateTime.valueOf();
    const _start_timestamp = Math.floor(start_timestamp / 1000);

    if (timeLimit > 120) {
      showToast(lang?.webinar_duration_greater_120_mins || "Webinar duration cannot exceed 120 minutes.", "error");
      return 0;
    }

    if (timeLimit < 0) {
      showToast(lang?.enter_correct_time_limit || "Please enter a valid duration.", "error");
      return 0;
    }
    const currentTime = moment().format("hh:mm A");
    const selectedDateTime = moment(`${scheduleDate}T${scheduleTime}`, 'YYYY-MM-DDTHH:mm');
    if (selectedDateTime.diff(currentTime, 'minutes') < 5) {
      showToast("Select at least 5 minutes after the current time.", "error");
      return;
    }
    if (combinedDateTime < new Date()) {
      showToast(lang?.start_time_less_current_time || "Start time cannot be in the past.", "error");
      return 0;
    }

    isValidFutureDate(scheduleDate);
    if (timeLimit < 5) {
      showToast(lang?.webinar_duration_greater_15_mins || "Webinar duration minimum 15 minutes", "error");
      return 0;
    }
    const coverList = Array.isArray(coverImg) ? coverImg : Object.values(coverImg || {});
    const data = {
      start_timestamp: _start_timestamp,
      type: "webinar",
      caption: description,
      schedule_date: scheduleDate,
      schedule_time: scheduleTime,
      category_id: "3",
      subcategory_id: category,
      title: title,
      visibility: visibility,
      duration: timeLimit,
      mediaBase64: coverList,
    };
    setIsLoading(true);
    onHide();
    try {
      const response = await axios.post(adminBaseURL + "user/post/create", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${getToken()}`,
        },
        onUploadProgress: (progressData) => {
          setProgress(Math.round((100 * progressData.loaded) / progressData.total));
        },
      });
      if (response?.data?.status) {
        setIsLoading(false);
        setInviteSpin(true);
        setWebinarId(response.data.post.id);
        handleInvitationList(response.data.post.id);

        setTitle("");
        setCategory("");
        setStartDate(null);
        setStartTime(null);
        setTimeLimit("");
        setVisibility("Public");
        setDescription("");
        return response;
      } else {
        showToast(response?.data?.error, "error");
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      showToast(error.message, "error");
    }
  };

  const handleUpdate = async () => {
    const combinedDateTimeString = `${scheduleDate} ${scheduleTime}`;
    const combinedDateTime = moment(combinedDateTimeString, "YYYY-MM-DD hh:mm A");
    const start_timestamp = combinedDateTime.valueOf();
    const _start_timestamp = Math.floor(start_timestamp / 1000);

    if (timeLimit > 120) {
      showToast(lang?.webinar_duration_greater_120_mins || "Webinar duration cannot exceed 120 minutes.", "error");
      return 0;
    }

    if (timeLimit < 0) {
      showToast(lang?.enter_correct_time_limit || "Please enter a valid duration.", "error");
      return 0;
    }
    if (combinedDateTime < new Date()) {
      showToast(lang?.start_time_less_current_time || "Start time cannot be in the past.", "error");
      return 0;
    }

    isValidFutureDate(scheduleDate);
    if (timeLimit < 15) {
      showToast(lang?.webinar_duration_greater_15_mins || "Webinar duration minimum 15 minutes", "error");
      return 0;
    }
    const coverList = Array.isArray(coverImg) ? coverImg : Object.values(coverImg || {});
    const data = {
      start_timestamp: _start_timestamp,
      type: "webinar",
      caption: description,
      schedule_date: scheduleDate,
      schedule_time: scheduleTime,
      category_id: "3",
      subcategory_id: category,
      title: title,
      visibility: visibility,
      duration: timeLimit,
      mediaBase64: coverList,
      post_id: selectedPost.id,
    };
    try {
      const response = await axios.post(adminBaseURL + "user/post/update", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response?.data?.status) {
        showToast(response?.data?.message || "Webinar updated successfully", "success");
        setEditWebinar(false);
        onHide();
        refetch();
        if (handlePostSelect) {
          handlePostSelect(response.data.data);
        }
        if (updatePostDetail) {
          updatePostDetail(response.data.data);
        }
      } else {
        showToast(response?.data?.error, "error");
      }
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const handleInvitationList = async (id) => {
    try {
      const url = adminBaseURL + `user/webinar/invitation-list/${id}?page=${invitePage}`;
      const response = await axios.get(url, authorize());
      if (inviteList.length) {
        setInviteList(inviteList.concat(response.data.data.data));
        setInviteData(response.data.data);
        setInvitePage(response.data.data.current_page + 1);
        return 0;
      }
      if (response.data.status) {
        setInviteList(response.data.data.data);
        setInviteData(response.data.data);
        setInvitePage(response.data.data.current_page + 1);
        setInviteSpin(false);
      } else {
        showToast(response.data.error, "error");
        setInviteSpin(false);
      }
    } catch (error) {
      console.error("Error occurred during the invitation:", error);
      return null;
    }
  };

  const searchWebinars = async (keywordQuery) => {
    try {
      const url = adminBaseURL + `user/webinar/invitation-list/${webinarId}?page=${searchPage}`;
      const params = { keyword: keywordQuery };
      const response = await axios.post(url, params, authorize());

      if (searchList.length) {
        setSearchList(searchList.concat(response.data.data.data));
        setSearchData(response.data.data);
        setSearchPage(response.data.data.current_page + 1);
        return 0;
      }
      if (response.data.status) {
        setSearchList(response.data.data.data);
        setSearchData(response.data.data);
        setSearchPage(response.data.data.current_page + 1);
        setInviteSpin(false);
      } else {
        showToast(response.data.error, "error");
        setInviteSpin(false);
      }
    } catch (error) {
      console.error("Error occurred during the search:", error);
      return null;
    }
  };

  const _futureTime = startDate
    ? moment(startDate).isSame(moment(), 'day')
      ? moment().add(5, 'minutes')
      : moment(startDate).startOf('day').add(5, 'minutes')
    : moment().add(5, 'minutes');

  const resetInviteList = useCallback(() => {
    setInvitePage(1);
    setInviteList([]);
    setInviteData({});
    handleInvitationList(webinarId);
  }, [webinarId]);

  const resetSearchList = useCallback(() => {
    setSearchPage(1);
    setSearchList([]);
    setSearchData({});
  }, []);

  useEffect(() => {
    if (keyword.length > 0) {
      setIsSearchMode(true);
      resetSearchList();
      searchWebinars(keyword);
    } else if (isSearchMode) {
      setIsSearchMode(false);
      resetInviteList();
    }
  }, [keyword, isSearchMode, resetSearchList, resetInviteList]);

  const debouncedSearchHandler = debounce((searchValue) => {
    setKeyword(searchValue);
  }, 500);

  useEffect(() => {
    return () => debouncedSearchHandler.cancel();
  }, [debouncedSearchHandler]);

  const _handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkPermission()) {
      return;
    }
    if (editWebinar) {
      await handleUpdate();
      return;
    }
    await handleApiCall(() => handleSubmit(), { type: "webinar", webinarId });
  };

  const onImageRemove = (index) => {
    const updatedImages = Array.isArray(coverImg) ? [...coverImg] : Object.values(coverImg || {});
    const updatedFileUploadRefs = { ...fileUploadRefs };
    updatedImages.splice(index, 1);

    if (fileUploadRefs[index + 1] && fileUploadRefs[index + 1].current) {
      fileUploadRefs[index + 1].current.value = '';
    }
    setImages(updatedImages);
    setCoverImg(updatedImages);
    const newFileUploadRefs = {};
    Object.keys(updatedFileUploadRefs)
      .filter((key) => key !== index.toString())
      .forEach((key, newIndex) => {
        newFileUploadRefs[newIndex] = updatedFileUploadRefs[key];
      });

    setFileUploadRefs(newFileUploadRefs);
    if (updatedImages.length === 0) {
      setHasSettings(true);
      setAR(undefined);
      setMinAR(undefined);
      setMaxAR(undefined);
      setWidth(500);
      setHeight(500);
      setTop(0);
      setLeft(0);
      setCropperOpen(false);
    }
  };

  const handleImageUploadClick = () => {
    const currentIndex = numberOfImages - 1;
    if (fileUploadRefs[currentIndex] && fileUploadRefs[currentIndex].current) {
      fileUploadRefs[currentIndex].current.value = '';
      fileUploadRefs[currentIndex].current.click();
    }
  };

  return (
    <>
      <div className="pf-form-body">
        {/* AI Banner */}
        <div className="pf-ai-banner">
          <div className="pf-ai-banner-left">
            <RiLiveLine className="pf-ai-banner-sparkle" size={16} />
            <span>{editWebinar ? "Edit Live Clinical Webinar" : "Schedule Live Medical Webinar"}</span>
          </div>
          <span className="pf-ai-banner-tag">Interactive HD Room</span>
        </div>

        <form onSubmit={_handleSubmit}>
          {/* Title & Category Grid */}
          <div className="pf-grid-2">
            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.title || "Webinar Title"}</span>
              </div>
              <input
                type="text"
                className="pf-input"
                placeholder={lang?.webinar_title || "e.g., Advanced Cataract Surgical Techniques"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.select_category || "Medical Subspecialty"}</span>
              </div>
              <select
                className="pf-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{ color: category === "" ? "#94A3B8" : "#0B132B" }}
              >
                <option value="" style={{ color: "#94A3B8" }}>
                  {lang?.webinar_category || "Select medical field"}
                </option>
                {subcategories.map((value) => (
                  <option key={value.id} value={value.id} style={{ color: "#0B132B" }}>
                    {value.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date & Time Schedule Card */}
          <div className="pf-card-section d-flex flex-column gap-2.5">
            <span className="fs-13 fw-600" style={{ color: "#0B132B" }}>Session Schedule</span>
            <div className="pf-grid-2">
              <div className="pf-field">
                <div className="pf-label">
                  <span className="pf-label-text">
                    <GoCalendar className="pf-label-icon" />
                    {lang?.start_date || "Date"}
                  </span>
                </div>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                    value={startDate}
                    defaultValue={tempDate}
                    minDate={moment().startOf('day')}
                    onChange={handleDateChange}
                    slotProps={{
                      dialog: { className: "pf-picker-dialog-webinar" },
                      popper: { className: "pf-picker-dialog-webinar" },
                      textField: {
                        placeholder: "MM/DD/YYYY",
                        className: "pf-mui-field",
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <GoCalendar style={{ cursor: 'pointer', color: '#EF4444', fontSize: '16px' }} />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="pf-field">
                <div className="pf-label">
                  <span className="pf-label-text">
                    <GoClock className="pf-label-icon" />
                    {lang?.start_time || "Start Time"}
                  </span>
                </div>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  {startDate ? (
                    <MobileTimePicker
                      value={startTime}
                      minTime={_futureTime}
                      onChange={handleTimeChange}
                      onOpen={handleTimePickerOpen}
                      slotProps={{
                        dialog: { className: "pf-picker-dialog-webinar" },
                        popper: { className: "pf-picker-dialog-webinar" },
                        textField: {
                          placeholder: "hh:mm aa",
                          className: "pf-mui-field",
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <GoClock style={{ cursor: 'pointer', color: '#EF4444', fontSize: '16px' }} />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  ) : (
                    <TextField
                      placeholder="hh:mm aa"
                      className="pf-mui-field"
                      onClick={() => handleTimeOpen("start_time")}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <GoClock style={{ cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </LocalizationProvider>
              </div>
            </div>
          </div>

          {/* Duration & Visibility Grid */}
          <div className="pf-grid-2">
            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.time_limit || "Session Duration"}</span>
                <span className="pf-optional-badge">15 to 120 mins</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  className="pf-input"
                  placeholder="Duration (e.g., 60)"
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(e.target.value)}
                />
                <span className="pf-chip-count" style={{ padding: "6px 12px", borderRadius: "10px", fontSize: "11.5px" }}>
                  Min
                </span>
              </div>
            </div>

            <div className="pf-field">
              <div className="pf-label">
                <span className="pf-label-text">{lang?.webinar_visibility || "Audience Access"}</span>
              </div>
              <div className="pf-segment-group">
                <button
                  type="button"
                  className={`pf-segment-btn ${visibility === 'Public' ? 'pf-segment-btn--active' : ''}`}
                  onClick={() => setVisibility('Public')}
                >
                  <GoGlobe size={13} />
                  <span>Public</span>
                </button>
                <button
                  type="button"
                  className={`pf-segment-btn ${visibility === 'Private' ? 'pf-segment-btn--active' : ''}`}
                  onClick={() => setVisibility('Private')}
                >
                  <GoLock size={13} />
                  <span>Private</span>
                </button>
              </div>
            </div>
          </div>

          {/* Description Field */}
          <div className="pf-field">
            <div className="pf-label">
              <span className="pf-label-text">{lang?.description || "Webinar Agenda & Clinical Objectives"}</span>
            </div>
            <textarea
              className="pf-textarea"
              rows={3}
              placeholder={lang?.webinar_description || "Outline the case studies, keynote speakers, and learning objectives..."}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Cover Image Upload (Dropzone if Empty, Thumbnails if Uploaded) */}
          {(() => {
            const coverList = Array.isArray(coverImg) ? coverImg : Object.values(coverImg || {});
            return coverList.length === 0 ? (
              <div className="pf-upload-zone" onClick={handleImageUploadClick}>
                <div className="pf-upload-zone-left">
                  <div className="pf-upload-icon-circle">
                    <RiImageAddLine size={20} />
                  </div>
                  <div>
                    <div className="pf-upload-zone-title">Upload Webinar Banner / Cover Photo</div>
                    <div className="pf-upload-zone-sub">High-resolution banner displayed on the events feed & invites</div>
                  </div>
                </div>
                <button type="button" className="pf-chip-btn">
                  Select Photo
                </button>
              </div>
            ) : (
              <div className="pf-thumb-strip">
                {coverList.map((img, index) => (
                  <div key={index} className="pf-thumb" style={{ width: "115px", height: "115px" }}>
                    <PhotoProvider
                      speed={() => 800}
                      easing={(type) =>
                        type === 2
                          ? 'cubic-bezier(0.36, 0, 0.66, -0.56)'
                          : 'cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }
                    >
                      <PhotoView src={editWebinar ? (img.startsWith('data:image/') ? img : _imagesURL + img) : img}>
                        <img
                          className="cursor"
                          src={editWebinar ? (img.startsWith('data:image/') ? img : _imagesURL + img) : img}
                          alt="Uploaded"
                          style={{ width: "115px", height: "115px", objectFit: "cover", display: "block" }}
                          onClick={() => handleImageClick(editWebinar ? (img.startsWith('data:image/') ? img : _imagesURL + img) : img)}
                        />
                      </PhotoView>
                    </PhotoProvider>
                    <button
                      type="button"
                      className="pf-thumb-remove"
                      aria-label="Remove"
                      onClick={() => onImageRemove(index)}
                    >
                      <IoCloseSharp size={14} />
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Bottom Actions (Clean cancel & submit only) */}
          <div className="pf-footer-row" style={{ justifyContent: "flex-end" }}>
            <div className="pf-footer-actions">
              <button
                type="button"
                className="pf-btn-cancel"
                onClick={() => {
                  onHide();
                  if (setEditWebinar) setEditWebinar(false);
                }}
              >
                {lang?.cancel || "Cancel"}
              </button>
              <button
                type="submit"
                className="pf-btn-submit"
                disabled={!(Array.isArray(coverImg) ? coverImg.length : Object.keys(coverImg || {}).length) || !title || !category || !startDate || !startTime || !timeLimit || !description || !visibility}
              >
                <span>{editWebinar ? (lang?.update || "Update Webinar") : (lang?.publish || "Schedule Live Webinar")}</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <Cropper
        images={images}
        setImages={setImages}
        imagesArr={coverImg}
        setImagesArr={setCoverImg}
        value={coverImg}
        max={maxNumber}
        allowCrop={true}
        ar={ar}
        setAR={setAR}
        minARatio={minARatio}
        setMaxAR={setMaxAR}
        maxARatio={maxARatio}
        setMinAR={setMinAR}
        resizable={resizable}
        setResizable={setResizable}
        handleClose={handleClose}
        fileUploadRefs={fileUploadRefs}
        setFileUploadRefs={setFileUploadRefs}
        numberOfImages={numberOfImages}
        setNumberOfImages={setNumberOfImages}
        hasSettings={hasSettings}
        setHasSettings={setHasSettings}
        updateCoordinatesCheck={updateCoordinatesCheck}
        setUpdateCoordinatesCheck={setUpdateCoordinatesCheck}
        height={height}
        setHeight={setHeight}
        width={width}
        setWidth={setWidth}
        top={top}
        setTop={setTop}
        left={left}
        setLeft={setLeft}
        cropperOpen={cropperOpen}
        setCropperOpen={setCropperOpen}
      />
      <ImageShowModal
        isOpen={isImagePreviewOpen}
        toggle={toggleImagePreview}
        imageSrc={previewImage}
      />
    </>
  );
};

export default WebinarTab;
